import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Checkbox,
  message,
  Space,
  Tag,
  Typography,
  Select,
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { captureError } from "../../lib/posthog";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface AdditionalCost {
  id: number;
  name: string;
  description: string;
  cost_eur: number | null;
  is_active: boolean;
  calculation_type: "fixed" | "user_input" | "percentage"; // Calculation type
  default_value: number | null; // Default value for user input
  max_value: number | null; // Maximum allowed value
  applies_to_countries: string[] | null; // Country-specific costs (NL, BE only)
  applies_to_incoterms: number[] | null;
  created_at: string;
  updated_at: string;
}

interface IncotermOption {
  id: number;
  name: string;
}

const AdditionalCostsTab: React.FC = () => {
  const [costs, setCosts] = useState<AdditionalCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCost, setEditingCost] = useState<AdditionalCost | null>(null);
  const [form] = Form.useForm();
  const [incoterms, setIncoterms] = useState<IncotermOption[]>([]);

  // Fetch additional costs (excluding variable costs like pickup fees)
  const fetchCosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("additional_costs")
        .select("*")
        .order("name");

      if (error) throw error;

      setCosts(data || []);
    } catch (error) {
      console.error("Error fetching additional costs:", error);
      message.error("Failed to load additional costs");
      captureError(error, {
        source: "AdditionalCostsTab",
        action: "fetchCosts",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCosts();
  }, []);

  useEffect(() => {
    const fetchIncoterms = async () => {
      try {
        const { data, error } = await supabase
          .from("bookings_incoterm")
          .select("id, name")
          .order("name");

        if (error) throw error;
        setIncoterms(data || []);
      } catch (error) {
        console.error("Error fetching incoterms:", error);
      }
    };
    fetchIncoterms();
  }, []);

  // Handle create/edit
  const handleSubmit = async (values: any) => {
    try {
      const costData = {
        name: values.name,
        description: values.description,
        cost_eur:
          values.calculation_type === "fixed"
            ? values.cost_eur ?? 0
            : null,
        calculation_type: values.calculation_type,
        default_value: values.default_value || 0,
        max_value: values.max_value || null,
        applies_to_countries:
          values.applies_to_countries && values.applies_to_countries.length > 0
            ? values.applies_to_countries
            : null,
        applies_to_incoterms:
          values.applies_to_incoterms && values.applies_to_incoterms.length > 0
            ? values.applies_to_incoterms
            : null,
        is_active: values.is_active !== undefined ? values.is_active : true,
      };

      if (editingCost) {
        // Update
        const { error } = await supabase
          .from("additional_costs")
          .update(costData)
          .eq("id", editingCost.id);

        if (error) throw error;
        message.success("Additional cost updated successfully");
      } else {
        // Create
        const { error } = await supabase
          .from("additional_costs")
          .insert([costData]);

        if (error) throw error;
        message.success("Additional cost created successfully");
      }

      setModalVisible(false);
      setEditingCost(null);
      form.resetFields();
      fetchCosts();
    } catch (error) {
      console.error("Error saving additional cost:", error);
      message.error("Failed to save additional cost");
      captureError(error, {
        source: "AdditionalCostsTab",
        action: "handleSubmit",
      });
    }
  };

  // Handle delete (deactivate)
  const handleToggleActive = async (cost: AdditionalCost) => {
    try {
      const { error } = await supabase
        .from("additional_costs")
        .update({ is_active: !cost.is_active })
        .eq("id", cost.id);

      if (error) throw error;

      message.success(
        `Additional cost ${
          cost.is_active ? "deactivated" : "activated"
        } successfully`
      );
      fetchCosts();
    } catch (error) {
      console.error("Error toggling additional cost status:", error);
      message.error("Failed to update additional cost status");
      captureError(error, {
        source: "AdditionalCostsTab",
        action: "handleToggleActive",
      });
    }
  };

  // Open modal for create
  const handleCreate = () => {
    setEditingCost(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Open modal for edit
  const handleEdit = (cost: AdditionalCost) => {
    setEditingCost(cost);
    form.setFieldsValue(cost);
    setModalVisible(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: AdditionalCost) => (
        <Space>
          {name}
          {!record.is_active && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "calculation_type",
      key: "calculation_type",
      render: (type: string) => {
        const colors = {
          fixed: "blue",
          user_input: "green",
          percentage: "orange",
        };
        const labels = {
          fixed: "Fixed",
          user_input: "Variable",
          percentage: "Percentage",
        };
        return (
          <Tag color={colors[type as keyof typeof colors] || "default"}>
            {labels[type as keyof typeof labels] || type}
          </Tag>
        );
      },
    },
    {
      title: "Cost/Value",
      key: "cost_display",
      render: (_: any, record: AdditionalCost) => {
        if (record.calculation_type === "percentage") {
          return `${record.default_value}%`;
        }
        if (record.calculation_type === "user_input") {
          if (record.default_value !== null && record.default_value !== undefined) {
            return `Default €${record.default_value.toFixed(2)}`;
          }
          return "User provided";
        }
        return `€${(record.cost_eur ?? 0).toFixed(2)}`;
      },
    },
    {
      title: "Countries",
      dataIndex: "applies_to_countries",
      key: "applies_to_countries",
      render: (countries: string[] | null) => {
        if (!countries || countries.length === 0) {
          return <Tag>All Countries</Tag>;
        }
        return countries.map((country) => <Tag key={country}>{country}</Tag>);
      },
    },
    {
      title: "Incoterms",
      dataIndex: "applies_to_incoterms",
      key: "applies_to_incoterms",
      render: (incotermIds: number[] | null) => {
        if (!incotermIds || incotermIds.length === 0) {
          return <Tag>All Incoterms</Tag>;
        }
        return incotermIds.map((id) => {
          const incoterm = incoterms.find((item) => item.id === id);
          return (
            <Tag key={id}>
              {incoterm?.name || `Incoterm ${id}`}
            </Tag>
          );
        });
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: AdditionalCost) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger={record.is_active}
            onClick={() => handleToggleActive(record)}
          >
            {record.is_active ? "Deactivate" : "Activate"}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Aanvullende Kosten Management
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Add Cost
            </Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
          Manage optional additional costs. These costs are NEVER automatically
          applied - users must explicitly select them via checkboxes in the
          calculator and booking forms. The country field is for informational
          purposes only.
        </Text>

        <Table
          columns={columns}
          dataSource={costs}
          loading={loading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <Modal
        title={editingCost ? "Edit Additional Cost" : "Add Additional Cost"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCost(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            calculation_type: "fixed",
            default_value: 0,
            is_active: true,
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input placeholder="e.g., Export Clearance License" />
          </Form.Item>

          <Form.Item
            name="calculation_type"
            label="Calculation Type"
            rules={[
              { required: true, message: "Please select a calculation type" },
            ]}
          >
            <Select style={{ width: "100%" }}>
              <Select.Option value="fixed">Fixed Amount</Select.Option>
              <Select.Option value="user_input">
                User Input (Variable)
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="default_value"
            label="Default Value"
            rules={[
              ({ getFieldValue }) => ({
                required: ["user_input", "percentage"].includes(
                  getFieldValue("calculation_type")
                ),
                message:
                  "Please enter a default value for user input and percentage costs",
              }),
            ]}
            extra="Required for user input and percentage costs. This is the default/pre-filled value users will see."
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="max_value"
            label="Maximum Value (Optional)"
            extra="Optional maximum limit for user input or percentage values"
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="Leave empty for no limit"
            />
          </Form.Item>

          <Form.Item
            name="applies_to_countries"
            label="Applies to Countries"
            extra="Informational only - indicates which countries this cost is relevant for. Costs are never automatically applied."
          >
            <Select
              mode="multiple"
              placeholder="Select countries (leave empty for all)"
              style={{ width: "100%" }}
            >
              <Select.Option value="NL">Netherlands (NL)</Select.Option>
              <Select.Option value="BE">Belgium (BE)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="applies_to_incoterms"
            label="Applies to Incoterms"
            extra="Leave empty to allow all incoterms. Otherwise select one or more."
          >
            <Select
              mode="multiple"
              placeholder="Select incoterms (leave empty for all)"
              style={{ width: "100%" }}
              loading={incoterms.length === 0}
            >
              {incoterms.map((incoterm) => (
                <Select.Option key={incoterm.id} value={incoterm.id}>
                  {incoterm.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="cost_eur"
            label="Fixed Cost (€)"
            rules={[
              ({ getFieldValue }) => ({
                required: getFieldValue("calculation_type") === "fixed",
                message: "Please enter a fixed cost amount",
              }),
            ]}
            extra="Required for fixed costs. Leave empty for user input and percentage costs."
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Optional description of this cost"
            />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <input type="checkbox" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingCost(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCost ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdditionalCostsTab;
